import {
  CurrenciesListListData,
  currenciesListListInfiniteOptions,
  Dropdown as ApiDropdown,
  InvoiceAmountReadable,
  InvoiceReadable,
  invoicesChangeStatusPartialUpdateMutation,
  invoicesPartialUpdateMutation,
  InvoiceStatus,
  PaginatedDropdownList
} from '@crm/api'
import { InfiniteSelect } from '@crm/components'
import { getAPIDateFormat, TagBillingStatuses } from '@crm/utils'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import {
  DEFAULT_DATE_FORMAT,
  format,
  showApiErrors,
  today
} from '@shared/utils'
import { QueryKey, useMutation } from '@tanstack/react-query'
import {
  Col,
  DatePicker,
  Descriptions,
  Dropdown,
  Flex,
  Form,
  Input,
  Modal,
  notification,
  Row,
  Spin,
  Tag,
  Typography
} from 'antd'
import { ArrowDown2 } from 'iconsax-react'
import { useEffect, useState } from 'react'

import type { DescriptionsProps, MenuProps } from 'antd'

const formName = 'change-to-paid'

type Props = {
  invoice: InvoiceReadable
  invoicesQueryKey: QueryKey
}

const { Text } = Typography

export const ChangeStatusLabel = ({ invoice, invoicesQueryKey }: Props) => {
  const { t } = useLocales()
  const [form] = Form.useForm()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const [localStatus, setLocalStatus] = useState<InvoiceStatus>()
  const [showModal, setShowModal] = useState(false)

  const { mutate: onChangeStatusMutate, isPending } = useMutation({
    ...invoicesChangeStatusPartialUpdateMutation(),
    onSuccess: () => {
      invalidateQueries(invoicesQueryKey)
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    },
    onError: (e) => showApiErrors(e)
  })

  const { mutate: onInvoiceUpdate, isPending: isInvoiceUpdateLoading } =
    useMutation({
      ...invoicesPartialUpdateMutation(),
      onSuccess: () => {
        invalidateQueries(invoicesQueryKey)
        notification.success({
          message: t('success.success'),
          description: t('success.success_data_save')
        })
      },
      onError: (e) => showApiErrors(e)
    })

  useEffect(() => {
    if (localStatus) {
      if (localStatus === InvoiceStatus.PAID) {
        setShowModal(true)

        return
      }

      onChangeStatusMutate({
        path: { id: Number(invoice.id) },
        body: { status: localStatus }
      })
    }
  }, [localStatus, invoice, onChangeStatusMutate])

  const onStatusChange = (status: InvoiceStatus) => setLocalStatus(status)

  const onModalClose = () => {
    setShowModal(false)
    setLocalStatus(undefined)
  }

  const onFormSubmit = async () => {
    const values = await form.validateFields()

    onModalClose()

    onInvoiceUpdate({
      path: { id: Number(invoice.id) },
      body: {
        status: localStatus,
        received_amount: Number(values.received_amount),
        pay_date: getAPIDateFormat(format(values.pay_date))
      }
    })

    onChangeStatusMutate({
      path: { id: invoice.id },
      body: { status: localStatus }
    })
  }

  const availableOptions = Object.values(InvoiceStatus).filter(
    (status) => status !== invoice.status
  )

  const billingAmountTitles = {
    quantity: t(`billing.quantity`),
    amount: t(`billing.amount`),
    tax: t(`billing.tax`),
    total: t(`billing.total`)
  }

  const dropdownItems: MenuProps['items'] = availableOptions.map((status) => ({
    key: status,
    label: t(`billing.${status}`),
    onClick: () => onStatusChange(status)
  }))

  const descriptionItems: DescriptionsProps['items'] = (
    Object.keys(billingAmountTitles) as (keyof InvoiceAmountReadable)[]
  ).map((title) => ({
    key: title,
    label: title,
    children: invoice?.amounts?.[0]?.[title]
  }))

  return (
    <>
      <Modal
        maskClosable={false}
        open={showModal}
        title={t('billing.confirm_amount')}
        cancelText={t('buttons.cancel')}
        onCancel={onModalClose}
        okText={t('buttons.save')}
        okButtonProps={{
          htmlType: 'submit',
          form: formName,
          loading: isPending || isInvoiceUpdateLoading
        }}
      >
        <Flex vertical gap={16}>
          <Flex gap={10}>
            <Text type="secondary">{t('billing.currency')}:</Text>

            <Text strong>{invoice?.currency?.name}</Text>
          </Flex>

          <Descriptions
            layout="vertical"
            bordered
            title={t('billing.amount')}
            items={descriptionItems}
            column={4}
          />

          <Form
            form={form}
            name={formName}
            onFinish={onFormSubmit}
            layout="vertical"
            initialValues={{
              received_currency: invoice.currency?.name,
              pay_date: today
            }}
          >
            <Row justify="space-between" gutter={12}>
              <Col span={8}>
                <Form.Item
                  name="received_amount"
                  label={t('billing.amount_received')}
                  rules={[
                    {
                      required: true,
                      message: t('billing.received_amount_required')
                    }
                  ]}
                >
                  <Input type="number" />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="received_currency"
                  label={t('billing.received_currency')}
                  rules={[
                    { required: true, message: t('billing.required_currency') }
                  ]}
                >
                  <InfiniteSelect<
                    CurrenciesListListData,
                    PaginatedDropdownList,
                    ApiDropdown
                  >
                    popupMatchSelectWidth
                    placeholder={t('billing.currency')}
                    getQueryFunction={currenciesListListInfiniteOptions}
                    disabled
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item name="pay_date" label={t('billing.pay_date')}>
                  <DatePicker allowClear format={DEFAULT_DATE_FORMAT} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Flex>
      </Modal>

      <Dropdown
        menu={{ items: dropdownItems }}
        trigger={['click']}
        placement="bottomRight"
        className="pointer"
      >
        <Tag color={TagBillingStatuses[invoice.status as InvoiceStatus]}>
          <Flex align="center" gap={4}>
            {isPending || isInvoiceUpdateLoading ? (
              <Spin size="small" />
            ) : (
              <ArrowDown2 size={16} />
            )}
            {t(`billing.${invoice.status}`)}
          </Flex>
        </Tag>
      </Dropdown>
    </>
  )
}
